import React, {useCallback, useRef, useState} from "react";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import PeriodInput from "../../../components/input/period-input.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import ShowHideColumnsGrid from "../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {FaEraser, FaPlus, FaQuestion, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import projectUtils from "../../../utils/project-utils.js";
import ScheduleAttendanceService from "../../../services/dispatch/schedule-attendance-service.js";
import DispatchListOptionsService from "../../../services/dispatch/dispatch-list-options-service.js";
import {
    sanitizeFilterValue,
    selectedValue,
    validateDateInterval,
} from "../utils/dispatch-list-utils.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const initialFilters = {
    dtStart: "",
    dtEnd: "",
    name: "",
    contingent: null,
    containedText: "",
    reason: [],
    address: "",
};

const buildFilter = (values) => [
    {field: "next_attendance", type: "keywords", value: {dtStart: values.dtStart || "", dtEnd: values.dtEnd || ""}},
    {field: "name", type: "like", value: sanitizeFilterValue(values.name)},
    {field: "createid", type: "=", value: selectedValue(values.contingent)},
    {field: "containedText", type: "like", value: sanitizeFilterValue(values.containedText)},
    {field: "address", type: "like", value: sanitizeFilterValue(values.address)},
    {field: "reason", type: "in", value: (values.reason ?? []).map(option => option.label ?? option.value)},
];

const HIDDEN_COLUMNS_STORAGE = "schedule-attendance-hidden-columns";
const DEFAULT_HIDDEN_COLUMNS = ["id"];

const baseColumns = [
    {field: "id", headerName: "ID", sort: "desc", width: 90, cellDataType: "number"},
    {field: "name", headerName: "Nome", minWidth: 220, flex: 1},
    {field: "status", headerName: "Status", minWidth: 120},
    {field: "next_attendance", headerName: "Próximo atendimento", minWidth: 180, comparator: aggridUtils.agGridCompareDateTime},
    {field: "user_created", headerName: "Cadastrado por", minWidth: 170},
    {field: "priority", headerName: "Prioridade", minWidth: 150},
    {field: "reason", headerName: "Natureza", minWidth: 240, flex: 1},
    {field: "address", headerName: "Endereço", minWidth: 360, flex: 1},
];

const buildColumnDefs = () => {
    const hiddenColumns = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE)) ?? DEFAULT_HIDDEN_COLUMNS;
    return baseColumns.map(column => ({...column, hide: hiddenColumns.includes(column.field)}));
};

const ScheduleAttendanceList = () => {
    const navigate = navigateHook();
    const gridRef = useRef(null);
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState([]);

    const {data = [], isLoading} = useQuery({
        queryFn: () => ScheduleAttendanceService.list({filter: [...filter]}),
        queryKey: ["dispatch-schedule-attendance", {filter}],
        placeholderData: keepPreviousData,
    });

    const {data: contingentOptions = [], isLoading: isLoadingContingents} = useQuery({
        queryFn: () => DispatchListOptionsService.contingents(),
        queryKey: ["dispatch-schedule-attendance-contingents"],
    });

    const {data: natureOptions = [], isLoading: isLoadingNatures} = useQuery({
        queryFn: () => DispatchListOptionsService.dispatchNatures(),
        queryKey: ["dispatch-natures"],
    });

    const [columnDefs, setColumnDefs] = useState(buildColumnDefs);

    const toggleColumn = (field) => {
        setColumnDefs(prev => prev.map(column => column.field === field ? {...column, hide: !column.hide} : column));
    };

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field);
    };

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const apply = useCallback(() => {
        const message = validateDateInterval(filterValues.dtStart, filterValues.dtEnd);
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        setFilter(buildFilter(filterValues));
    }, [filterValues]);

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, []);

    const openForm = () => navigate(
        API_DESPACHO_WEB + "/schedule-attendance/form",
        false,
    );

    const onRowClicked = ({data: row}) => {
        if (!row?.id) return;

        navigate(
            `${API_DESPACHO_WEB}/schedule-attendance/form/${row.id}`,
            true,
        );
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <Template isLoading={isLoading || isLoadingContingents || isLoadingNatures}>
            <Template.Header>
                <Template.Title>Agendamentos</Template.Title>
                <div className={"flex gap-2"}>
                    <Button onClick={openForm}><FaPlus/>Adicionar agendamento</Button>
                </div>
            </Template.Header>
            <Template.Body className="flex flex-col h-full min-h-0 space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow className="items-start">
                            <PeriodInput
                                startDateId="dtStart"
                                endDateId="dtEnd"
                                label="Próximo agendamento entre"
                                dateFormat="LL"
                                returnDateObj={false}
                                startDate={filterValues.dtStart}
                                endDate={filterValues.dtEnd}
                                setFilter={setFilterValues}
                                cols={5}
                            />
                            <InputForm classNameWrapper="col-span-4" id="name" label="Nome" value={filterValues.name} onChange={handleInput}/>
                            <SelectComponent
                                classNameWrapper="col-span-3"
                                id="contingent"
                                label="Cadastrado por"
                                options={contingentOptions}
                                value={filterValues.contingent}
                                onChange={(option) => setFilterValues(prev => ({...prev, contingent: option}))}
                            />

                            <InputForm
                                classNameWrapper="col-span-4"
                                id="containedText"
                                label="Texto contido"
                                inputClass="pr-9"
                                title="Filtros: Descrição"
                                value={filterValues.containedText}
                                onChange={handleInput}
                                span={
                                    <span
                                        className="absolute right-2 top-1/2 z-20 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-[#E9ECEF] text-[#676a6c]"
                                        title="Filtros: Descrição"
                                        aria-label="Campos pesquisados pelo filtro Texto contido"
                                    >
                                        <FaQuestion size={10}/>
                                    </span>
                                }
                            />
                            <SelectComponent
                                classNameWrapper="col-span-3"
                                id="reason"
                                label="Natureza"
                                isMulti
                                wrapValues
                                options={natureOptions}
                                value={filterValues.reason}
                                onChange={(options) => setFilterValues(prev => ({...prev, reason: options ?? []}))}
                            />
                            <div className={"col-span-5 flex gap-4"}>
                                <InputForm classNameWrapper="w-full" id="address" label="Endereço" value={filterValues.address} onChange={handleInput}/>
                                <div className="flex gap-2 justify-end">
                                    <Button onClick={clear} title="Limpar" target="primary-outline"><FaEraser/></Button>
                                    <Button onClick={apply} title="Buscar"><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-197px)] aggrid-row-pointer">
                            <AgGridReact
                                ref={gridRef}
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                onColumnVisible={onColumnVisible}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>

                        <ShowHideColumnsGrid
                            columnDefs={columnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={HIDDEN_COLUMNS_STORAGE}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default ScheduleAttendanceList;
